import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
const Withdrawal = () => {
    const [withdrawals, setWithdrawals] = useState([]);
    const [amount, setAmount] = useState(1000);
    const [balance, setBalance] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showBalance, setShowBalance] = useState(true);

    useEffect(() => {
        fetchWithdrawalData();
    }, []);

    const fetchWithdrawalData = async () => {
        try {
            const userId = localStorage.getItem('userid');
            const [withdrawalsRes, balanceRes] = await Promise.all([
                axios.get(`https://singlewindowsecurities.com/Rest3/withdrawals_api/${userId}`),
                axios.get(`https://singlewindowsecurities.com/Rest3/wallet_api/${userId}`)
            ]);
            setWithdrawals(withdrawalsRes.data);
            setBalance(balanceRes.data.wallet || 0);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const handleCancelRequest = async (withdrawalId) => {
        if (window.confirm("Are you sure you want to cancel this withdrawal request?")) {
            try {
                await axios.get(`https://singlewindowsecurities.com/Rest3/cancel_withdrawal_api/${withdrawalId}`);
                alert("Withdrawal request cancelled successfully");
                fetchWithdrawalData();
            } catch (error) {
                console.error("Error cancelling withdrawal:", error);
                alert("Failed to cancel withdrawal request");
            }
        }
    };

    const handleSubmit = async () => {
        if (amount < 1000) {
            alert("Minimum withdrawal amount is ₹1000");
            return;
        }
        if (amount > balance) {
            alert("Insufficient balance for withdrawal");
            return;
        }

        setIsSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('withdrawAmt', amount);
            
            const response = await axios.post(
                `https://singlewindowsecurities.com/Rest3/web_withdraw_submit_api/${localStorage.getItem('userid')}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            alert(response.data.msg);
            fetchWithdrawalData();
            setAmount(1000);
        } catch (error) {
            console.error("Withdrawal error:", error);
            alert("Could not process withdrawal request");
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStatusColor = (status) => {
        switch(status?.toLowerCase()) {
            case 'approved': return '#00C853';
            case 'pending': return '#D4AF37';
            case 'rejected': return '#FF5252';
            case 'cancelled': return '#888';
            default: return '#888';
        }
    };

    const getStatusBadge = (status) => {
        return (
            <span style={{
                background: `rgba(${getStatusColor(status) === '#00C853' ? '0,200,83' : getStatusColor(status) === '#D4AF37' ? '212,175,55' : getStatusColor(status) === '#FF5252' ? '255,82,82' : '136,136,136'}, 0.15)`,
                color: getStatusColor(status),
                padding: '4px 10px',
                borderRadius: '20px',
                fontSize: '11px',
                fontWeight: 'bold'
            }}>
                {status}
            </span>
        );
    };

    const formatIndianCurrency = (num) => {
        if (!num && num !== 0) return '0.00';
        return parseFloat(num).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    return (
        <div className="withdrawal-page" style={{ 
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0d0d0d 100%)'
        }}>
            <Helmet>
                <title>Withdraw Funds | Single Window Securities</title>
            </Helmet>

            {/* Header */}
            <div style={{
                background: 'rgba(10, 10, 10, 0.95)',
                backdropFilter: 'blur(10px)',
                borderBottom: '1px solid rgba(212, 175, 55, 0.3)',
                position: 'sticky',
                top: 0,
                zIndex: 100
            }}>
                <div className="container py-3">
                    <div className="d-flex align-items-center justify-content-between">
                        <Link to="/dashboard" className="btn" style={{
                            background: 'rgba(212, 175, 55, 0.1)',
                            border: '1px solid rgba(212, 175, 55, 0.3)',
                            borderRadius: '12px',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#D4AF37'
                        }}>
                            <i className="fas fa-arrow-left"></i>
                        </Link>
                        <h5 className="fw-bold mb-0" style={{ color: '#D4AF37' }}>Withdraw Funds</h5>
                        <div style={{ width: '36px' }}></div>
                    </div>
                </div>
            </div>

            <main className="withdrawal-content" style={{ padding: '16px' }}>
                {/* Balance Info */}
                <div style={{
                    background: 'linear-gradient(145deg, rgba(212, 175, 55, 0.15), rgba(184, 150, 12, 0.05))',
                    borderRadius: '24px',
                    padding: '24px',
                    border: '1px solid rgba(212, 175, 55, 0.2)',
                    marginBottom: '20px'
                }}>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                        <small style={{ color: '#D4AF37', letterSpacing: '1px' }}>AVAILABLE BALANCE</small>
                        <button 
                            className="btn btn-sm p-0"
                            onClick={() => setShowBalance(!showBalance)}
                            style={{ color: '#D4AF37' }}
                        >
                            <i className={`fas fa-eye${showBalance ? '' : '-slash'}`}></i>
                        </button>
                    </div>
                    <h2 className="fw-bold mb-0" style={{ color: '#D4AF37' }}>
                        {showBalance ? `₹${formatIndianCurrency(balance)}` : '••••••'}
                    </h2>
                </div>

                {/* Withdrawal Form */}
                <div style={{
                    background: 'rgba(20, 20, 20, 0.95)',
                    borderRadius: '24px',
                    border: '1px solid rgba(212, 175, 55, 0.2)',
                    padding: '20px',
                    marginBottom: '20px'
                }}>
                    <h6 style={{ color: '#D4AF37', marginBottom: '16px' }}>Withdrawal Amount</h6>
                    
                    <div className="mb-3">
                        <div className="input-group">
                            <span className="input-group-text" style={{
                                background: '#1a1a1a',
                                border: '1px solid rgba(212, 175, 55, 0.3)',
                                borderRight: 'none',
                                color: '#D4AF37',
                                fontWeight: 'bold'
                            }}>₹</span>
                            <input
                                type="number"
                                className="form-control"
                                value={amount}
                                onChange={(e) => setAmount(Number(e.target.value))}
                                placeholder="Enter amount"
                                min="1000"
                                step="100"
                                style={{
                                    background: '#1a1a1a',
                                    border: '1px solid rgba(212, 175, 55, 0.3)',
                                    borderLeft: 'none',
                                    color: '#fff'
                                }}
                            />
                        </div>
                        <small style={{ color: '#888', fontSize: '11px' }} className="mt-1 d-block">
                            Minimum withdrawal amount: ₹1,000
                        </small>
                    </div>
                    
                    <div className="d-flex gap-2 mb-4">
                        <button 
                            className="btn flex-fill py-2"
                            onClick={() => setAmount(1000)}
                            style={{
                                background: 'rgba(212, 175, 55, 0.1)',
                                border: '1px solid rgba(212, 175, 55, 0.3)',
                                borderRadius: '12px',
                                color: '#D4AF37',
                                fontWeight: 'bold'
                            }}
                        >
                            ₹1,000
                        </button>
                        <button 
                            className="btn flex-fill py-2"
                            onClick={() => setAmount(5000)}
                            style={{
                                background: 'rgba(212, 175, 55, 0.1)',
                                border: '1px solid rgba(212, 175, 55, 0.3)',
                                borderRadius: '12px',
                                color: '#D4AF37',
                                fontWeight: 'bold'
                            }}
                        >
                            ₹5,000
                        </button>
                        <button 
                            className="btn flex-fill py-2"
                            onClick={() => setAmount(10000)}
                            style={{
                                background: 'rgba(212, 175, 55, 0.1)',
                                border: '1px solid rgba(212, 175, 55, 0.3)',
                                borderRadius: '12px',
                                color: '#D4AF37',
                                fontWeight: 'bold'
                            }}
                        >
                            ₹10,000
                        </button>
                    </div>
                    
                    <button 
                        className="btn w-100 py-3"
                        onClick={handleSubmit}
                        disabled={isSubmitting || amount < 1000 || amount > balance}
                        style={{
                            background: (isSubmitting || amount < 1000 || amount > balance) ? '#333' : 'linear-gradient(145deg, #D4AF37, #B8960C)',
                            border: 'none',
                            borderRadius: '16px',
                            color: (isSubmitting || amount < 1000 || amount > balance) ? '#888' : '#000',
                            fontWeight: 'bold',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                            if (!isSubmitting && amount >= 1000 && amount <= balance) {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 5px 20px rgba(212, 175, 55, 0.3)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                        }}
                    >
                        {isSubmitting ? (
                            <>
                                <i className="fas fa-spinner fa-spin me-2"></i> Processing...
                            </>
                        ) : (
                            "REQUEST WITHDRAWAL"
                        )}
                    </button>
                </div>

                {/* Withdrawal History */}
                <div style={{
                    background: 'rgba(20, 20, 20, 0.95)',
                    borderRadius: '24px',
                    border: '1px solid rgba(212, 175, 55, 0.2)',
                    padding: '20px'
                }}>
                    <h6 style={{ color: '#D4AF37', marginBottom: '16px' }}>Withdrawal History</h6>
                    
                    {withdrawals.length === 0 ? (
                        <div className="text-center py-4">
                            <i className="fas fa-history" style={{ color: '#D4AF37', fontSize: '40px', opacity: 0.5 }}></i>
                            <p className="mt-2" style={{ color: '#888' }}>No withdrawal history found</p>
                        </div>
                    ) : (
                        <div>
                            {withdrawals.map((withdrawal) => (
                                <div key={withdrawal.wid} className="d-flex justify-content-between align-items-center p-3 mb-2" style={{
                                    background: '#1a1a1a',
                                    borderRadius: '12px',
                                    border: '1px solid rgba(212, 175, 55, 0.1)'
                                }}>
                                    <div>
                                        <div className="fw-bold" style={{ color: '#D4AF37' }}>₹{formatIndianCurrency(withdrawal.wamt)}</div>
                                        <small style={{ color: '#888' }}>{withdrawal.req_date}</small>
                                        {withdrawal.remarks && (
                                            <div><small style={{ color: '#666' }}>{withdrawal.remarks}</small></div>
                                        )}
                                    </div>
                                    <div className="text-end">
                                        {getStatusBadge(withdrawal.req_status)}
                                        {withdrawal.req_status !== 'Approved' && withdrawal.req_status !== 'Rejected' && (
                                            <button 
                                                className="btn btn-sm mt-2 d-block"
                                                onClick={() => handleCancelRequest(withdrawal.wid)}
                                                style={{
                                                    background: 'rgba(255, 82, 82, 0.15)',
                                                    color: '#FF5252',
                                                    borderRadius: '8px',
                                                    fontSize: '11px'
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Info Note */}
                <div className="mt-3 text-center">
                    <small style={{ color: '#555' }}>
                        <i className="fas fa-clock me-1"></i>
                        Withdrawals are processed within 24-48 hours
                    </small>
                </div>
            </main>

            <style>
                {`
                    input:focus {
                        box-shadow: 0 0 0 2px rgba(212, 175, 55, 0.3) !important;
                        outline: none;
                        border-color: #D4AF37 !important;
                    }
                    input[type="number"]::-webkit-inner-spin-button,
                    input[type="number"]::-webkit-outer-spin-button {
                        opacity: 0.5;
                    }
                `}
            </style>
        </div>
    );
};

export default Withdrawal;